import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Trash2 } from 'lucide-react';
import { Alert, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, LoadingState, PageHeader, Select, Textarea, useToast } from '@buzzbee/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { FeatureGate } from '../components/common';
import { LANGUAGES } from '../components/editors';
import { DepositSettings, RebookingSettings } from '../components/rebooking-deposits';
import { del, errorMessage, formatWhen, get, post, put } from '../lib/api';
import { hasFeature, useWorkspace, type WorkspaceSummary } from '../lib/session';

const SECTIONS: { key: string; label: string; feature: string; render: (ws: WorkspaceSummary) => ReactNode }[] = [
  { key: 'analytics', label: 'Analytics and value report', feature: 'analytics', render: () => <Analytics /> },
  { key: 'chat', label: 'Website chat', feature: 'web_chat', render: () => <ChatWidget /> },
  { key: 'sms', label: 'Text messages', feature: 'sms_confirmations', render: () => <Sms /> },
  { key: 'transfer', label: 'Live transfer', feature: 'call_transfer', render: () => <Transfer /> },
  { key: 'languages', label: 'Languages', feature: 'multilingual', render: () => <Languages /> },
  { key: 'reminders', label: 'Reminders and no-shows', feature: 'reminders', render: (ws) => <Reminders ws={ws} /> },
  { key: 'deposits', label: 'Deposits', feature: 'no_show_rescue', render: () => <DepositSettings /> },
  { key: 'reviews', label: 'Review requests', feature: 'review_requests', render: () => <Reviews /> },
  { key: 'rebooking', label: 'Rebooking reminders', feature: 'review_requests', render: () => <RebookingSettings /> },
  { key: 'crm', label: 'CRM sync', feature: 'crm_sync', render: () => <Crm /> },
  { key: 'workflows', label: 'Custom workflows', feature: 'custom_workflows', render: () => <Workflows /> },
  { key: 'support', label: 'Priority support', feature: 'priority_support', render: () => <PrioritySupport /> },
];

function useSave(fn: (v: any) => Promise<unknown>, label = 'Saved') {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({ mutationFn: fn, onSuccess: () => { success(label); void qc.invalidateQueries(); }, onError: (e) => error('Could not save', errorMessage(e)) });
}

function Panel({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

export function FrontDeskPage() {
  const ws = useWorkspace();
  const [active, setActive] = useState(SECTIONS.find((s) => hasFeature(ws, s.feature))?.key ?? 'analytics');
  const section = SECTIONS.find((s) => s.key === active)!;
  return (
    <div className="space-y-6">
      <PageHeader title="Front desk" description={ws.brand.product === 'buzzbee' ? 'More ways your receptionist can work for you. Locked features are part of higher plans.' : 'Everything your premium front desk does beyond answering calls.'} />
      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        <nav aria-label="Front desk features" className="flex gap-1 overflow-x-auto lg:flex-col">
          {SECTIONS.map((s) => (
            <button key={s.key} type="button" onClick={() => setActive(s.key)} className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm ${active === s.key ? 'bg-card font-semibold shadow-card' : 'text-muted-foreground hover:bg-foreground/[0.04]'}`}>
              <span className="flex-1">{s.label}</span>
              {hasFeature(ws, s.feature) ? null : <Lock className="size-3.5" aria-label="Locked" />}
            </button>
          ))}
        </nav>
        <div>
          <FeatureGate ws={ws} feature={section.feature}>
            {section.render(ws)}
          </FeatureGate>
        </div>
      </div>
    </div>
  );
}

function Analytics() {
  const ws = useWorkspace();
  const a = useQuery({ queryKey: ['analytics'], queryFn: () => get('/api/analytics?days=30') });
  const r = useQuery({ queryKey: ['value-report'], queryFn: () => get('/api/reports/value?days=30') });
  const email = useSave(() => post('/api/reports/value/email'), 'Report emailed to owners');
  if (a.isLoading || r.isLoading) return <LoadingState />;
  const max = Math.max(1, ...(a.data?.series ?? []).map((d: any) => d.calls));
  const rep = r.data?.report;
  const $ = (c: number) => `$${Math.round(c / 100).toLocaleString('en-CA')}`;
  return (
    <div className="space-y-6">
      <Panel title="Last 30 days" description={`Booking conversion: ${Math.round((a.data?.conversionRate ?? 0) * 100)}% of calls.`}>
        <div className="flex h-40 items-end gap-1" role="img" aria-label="Calls per day for the last 30 days">
          {a.data?.series.map((d: any) => (
            <div key={d.date} className="flex flex-1 flex-col justify-end" title={`${d.date}: ${d.calls} calls, ${d.bookings} bookings`}>
              <div className="rounded-t bg-primary/80" style={{ height: `${(d.calls / max) * 100}%`, minHeight: d.calls ? 4 : 0 }} />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          {Object.entries(a.data?.outcomes ?? {}).map(([k, v]) => (
            <Badge key={k} variant="outline">
              {k.replace('_', ' ')}: {String(v)}
            </Badge>
          ))}
          {Object.entries(a.data?.languages ?? {}).map(([k, v]) => (
            <Badge key={k}>
              {LANGUAGES.find((l) => l.code === k)?.name ?? k}: {String(v)}
            </Badge>
          ))}
        </div>
      </Panel>
      {rep ? (
        <Panel title="Monthly value report" description={`Sent to owners on the 1st of each month. Revenue uses ${$(rep.avgBookingValueCents)} per booking.`}>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><p className="text-xs text-muted-foreground">Calls answered</p><p className="figures text-2xl font-semibold">{rep.callsAnswered}</p></div>
            <div><p className="text-xs text-muted-foreground">Bookings ({rep.afterHoursBookings} after hours)</p><p className="figures text-2xl font-semibold">{rep.bookings}</p></div>
            <div><p className="text-xs text-muted-foreground">Estimated revenue from AI bookings</p><p className="figures text-2xl font-semibold">{$(rep.estimatedRevenueCents)}</p></div>
          </div>
          <Button variant="outline" onClick={() => email.mutate(null)} loading={email.isPending} disabled={ws.role === 'staff'}>
            Email it to me now
          </Button>
        </Panel>
      ) : null}
    </div>
  );
}

function ChatWidget() {
  const q = useQuery({ queryKey: ['chat-widget'], queryFn: () => get('/api/chat-widget') });
  const [f, setF] = useState({ greeting: '', disclosure: '', accentColor: '#1f5f63', allowedOrigins: '' });
  useEffect(() => {
    if (q.data) setF({ greeting: q.data.greeting, disclosure: q.data.disclosure, accentColor: q.data.accentColor, allowedOrigins: q.data.allowedOrigins.join('\n') });
  }, [q.data]);
  const save = useSave(() => put('/api/chat-widget', { ...f, allowedOrigins: f.allowedOrigins.split(/\s+/).filter(Boolean) }));
  if (!q.data) return <LoadingState />;
  return (
    <Panel title="Website chat widget" description="Paste this snippet before </body> on your website. It answers questions and books appointments with the same rules as your phone receptionist.">
      <Textarea readOnly rows={2} className="figures text-xs" value={q.data.snippet} aria-label="Embed snippet" />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="cw-g">Greeting</Label><Input id="cw-g" value={f.greeting} onChange={(e) => setF({ ...f, greeting: e.target.value })} /></div>
        <div className="space-y-1.5"><Label htmlFor="cw-c">Accent colour</Label><Input id="cw-c" value={f.accentColor} onChange={(e) => setF({ ...f, accentColor: e.target.value })} /></div>
      </div>
      <div className="space-y-1.5"><Label htmlFor="cw-d">AI disclosure shown in the chat</Label><Input id="cw-d" value={f.disclosure} onChange={(e) => setF({ ...f, disclosure: e.target.value })} /></div>
      <div className="space-y-1.5"><Label htmlFor="cw-o">Allowed websites (one per line, empty = any)</Label><Textarea id="cw-o" rows={2} value={f.allowedOrigins} onChange={(e) => setF({ ...f, allowedOrigins: e.target.value })} placeholder="https://www.example.ca" /></div>
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>Save</Button>
    </Panel>
  );
}

function Sms() {
  const q = useQuery({ queryKey: ['sms'], queryFn: () => get('/api/sms') });
  const log = useQuery({ queryKey: ['sms-log'], queryFn: () => get('/api/sms/log') });
  const [f, setF] = useState({ starts: '21:00', ends: '08:00', ownerSmsPhone: '' });
  useEffect(() => {
    if (q.data) setF({ starts: q.data.quietHours?.starts ?? '21:00', ends: q.data.quietHours?.ends ?? '08:00', ownerSmsPhone: q.data.ownerSmsPhone ?? '' });
  }, [q.data]);
  const save = useSave(() => put('/api/sms', { quietHours: { starts: f.starts, ends: f.ends }, ownerSmsPhone: f.ownerSmsPhone || null }));
  return (
    <div className="space-y-6">
      <Panel title="Text messages" description="Booking confirmations go only to customers who said yes on the call (CASL/TCPA consent is recorded). STOP, HELP and START are handled automatically. Nothing is texted during quiet hours.">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5"><Label htmlFor="qh-s">Quiet hours start</Label><Input id="qh-s" type="time" value={f.starts} onChange={(e) => setF({ ...f, starts: e.target.value })} /></div>
          <div className="space-y-1.5"><Label htmlFor="qh-e">Quiet hours end</Label><Input id="qh-e" type="time" value={f.ends} onChange={(e) => setF({ ...f, ends: e.target.value })} /></div>
          <div className="space-y-1.5"><Label htmlFor="owner-sms">Text me new bookings at</Label><Input id="owner-sms" value={f.ownerSmsPhone} onChange={(e) => setF({ ...f, ownerSmsPhone: e.target.value })} placeholder="+1 416 555 0199" /></div>
          <Button onClick={() => save.mutate(null)} loading={save.isPending}>Save</Button>
        </div>
      </Panel>
      <Panel title="Recent texts">
        {log.data?.notifications.length === 0 ? <p className="text-sm text-muted-foreground">No texts yet.</p> : null}
        <div className="divide-y text-sm">
          {log.data?.notifications.map((n: any) => (
            <div key={n.id} className="flex flex-wrap gap-2 py-2">
              <Badge variant={n.status === 'sent' ? 'success' : 'default'}>{n.status}{n.reason ? `: ${n.reason.replace('_', ' ')}` : ''}</Badge>
              <span className="figures text-muted-foreground">{n.to}</span>
              <span className="min-w-0 flex-1 truncate">{n.body}</span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function Transfer() {
  const q = useQuery({ queryKey: ['transfer'], queryFn: () => get('/api/transfer') });
  const [phone, setPhone] = useState('');
  useEffect(() => setPhone(q.data?.phone ?? ''), [q.data]);
  const save = useSave(() => put('/api/transfer', { phone: phone || null }));
  return (
    <Panel title="Live transfer" description="During opening hours, callers who ask for a person (or have an emergency) are connected to this number. After hours the receptionist takes a message.">
      <div className="flex items-end gap-2">
        <div className="space-y-1.5"><Label htmlFor="tr">Transfer to</Label><Input id="tr" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 416 555 0100" /></div>
        <Button onClick={() => save.mutate(null)} loading={save.isPending}>Save</Button>
      </div>
    </Panel>
  );
}

function Languages() {
  const q = useQuery({ queryKey: ['languages'], queryFn: () => get('/api/languages') });
  const [langs, setLangs] = useState<string[]>([]);
  useEffect(() => setLangs(q.data?.languages ?? []), [q.data]);
  const save = useSave(() => put('/api/languages', { languages: langs }));
  return (
    <Panel title="Languages" description="Your receptionist starts in your main language and switches when the caller speaks or asks for another. The disclosure is restated in the new language.">
      <div className="flex flex-wrap gap-4">
        {LANGUAGES.filter((l) => l.code !== q.data?.defaultLanguage).map((l) => (
          <label key={l.code} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={langs.includes(l.code)} onChange={(e) => setLangs(e.target.checked ? [...langs, l.code] : langs.filter((x) => x !== l.code))} /> {l.name}
          </label>
        ))}
      </div>
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>Save</Button>
    </Panel>
  );
}

function Reminders({ ws }: { ws: WorkspaceSummary }) {
  const r = useQuery({ queryKey: ['reminders'], queryFn: () => get('/api/reminders') });
  const n = useQuery({ queryKey: ['no-show'], queryFn: () => get('/api/no-show') });
  const [windows, setWindows] = useState('24, 2');
  useEffect(() => {
    if (r.data) setWindows(r.data.windowsHours.join(', '));
  }, [r.data]);
  const save = useSave(() => put('/api/reminders', { windowsHours: windows.split(',').map((x) => Number(x.trim())).filter((x) => x > 0) }));
  return (
    <div className="space-y-6">
      <Panel title="Appointment reminders" description="Texts before each appointment to customers who opted in, never during quiet hours.">
        <div className="flex items-end gap-2">
          <div className="space-y-1.5"><Label htmlFor="rw">Hours before the appointment</Label><Input id="rw" value={windows} onChange={(e) => setWindows(e.target.value)} /></div>
          <Button onClick={() => save.mutate(null)} loading={save.isPending}>Save</Button>
        </div>
        <p className="text-sm text-muted-foreground">{r.data?.log.length ?? 0} reminders logged recently.</p>
      </Panel>
      <Panel title="No-show rescue" description="When you mark a booking as a no-show, the customer gets one friendly text offering a new time.">
        <div className="divide-y text-sm">
          {n.data?.noShows.length === 0 ? <p className="text-muted-foreground">No no-shows. Nice.</p> : null}
          {n.data?.noShows.map((a: any) => (
            <div key={a.id} className="flex gap-2 py-2">
              <span className="figures w-40">{formatWhen(a.startsAt, ws.timezone)}</span>
              <span className="flex-1">{a.serviceName} · {a.contactName ?? 'Customer'}</span>
              <Badge variant={a.rescueSentAt ? 'success' : 'default'}>{a.rescueSentAt ? 'Rescue sent' : 'Pending'}</Badge>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

function Reviews() {
  const q = useQuery({ queryKey: ['reviews'], queryFn: () => get('/api/reviews') });
  const [f, setF] = useState({ link: '', delayHours: 2, frequencyCapDays: 90, message: '' });
  useEffect(() => {
    if (q.data) setF({ link: q.data.link ?? '', delayHours: q.data.delayHours, frequencyCapDays: q.data.frequencyCapDays, message: q.data.message ?? '' });
  }, [q.data]);
  const save = useSave(() => put('/api/reviews', { link: f.link || null, delayHours: Number(f.delayHours), frequencyCapDays: Number(f.frequencyCapDays), message: f.message || null }));
  return (
    <Panel title="Review requests" description={q.data?.policy}>
      <div className="space-y-1.5"><Label htmlFor="rv-l">Your review link (e.g. Google)</Label><Input id="rv-l" value={f.link} onChange={(e) => setF({ ...f, link: e.target.value })} placeholder="https://g.page/r/..." /></div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label htmlFor="rv-d">Hours after the appointment</Label><Input id="rv-d" type="number" min={0} value={f.delayHours} onChange={(e) => setF({ ...f, delayHours: Number(e.target.value) })} /></div>
        <div className="space-y-1.5"><Label htmlFor="rv-c">Ask each customer at most every (days)</Label><Input id="rv-c" type="number" min={1} value={f.frequencyCapDays} onChange={(e) => setF({ ...f, frequencyCapDays: Number(e.target.value) })} /></div>
      </div>
      <div className="space-y-1.5"><Label htmlFor="rv-m">Custom message (optional, include {'{{link}}'})</Label><Textarea id="rv-m" rows={2} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></div>
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>Save</Button>
      <p className="text-sm text-muted-foreground">{q.data?.log.length ?? 0} requests logged recently.</p>
    </Panel>
  );
}

function Crm() {
  const q = useQuery({ queryKey: ['crm'], queryFn: () => get('/api/crm') });
  const [provider, setProvider] = useState('hubspot');
  const connect = useSave(() => post('/api/crm/connect', { provider }), 'Connected');
  const disconnect = useSave(() => post('/api/crm/disconnect'), 'Disconnected');
  const sync = useSave(() => post('/api/crm/sync'), 'Synced');
  const live = (q.data?.liveProviders ?? []) as string[];
  return (
    <Panel title="CRM sync" description="New callers who book are created or updated in your CRM automatically.">
      {q.data?.integration ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          Connected to <strong className="capitalize">{q.data.integration.provider}</strong>
          {live.includes(q.data.integration.provider) ? <Badge variant="success">live</Badge> : <Badge>recording only until an API token is configured</Badge>}
          <Button size="sm" variant="outline" onClick={() => sync.mutate(null)} loading={sync.isPending}>Sync now</Button>
          <Button size="sm" variant="ghost" onClick={() => disconnect.mutate(null)}>Disconnect</Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <div className="space-y-1.5">
            <Label htmlFor="crm-p">CRM</Label>
            <Select id="crm-p" value={provider} onChange={(e) => setProvider(e.target.value)} options={[{ value: 'hubspot', label: 'HubSpot' }, { value: 'salesforce', label: 'Salesforce (coming soon)' }, { value: 'pipedrive', label: 'Pipedrive (coming soon)' }, { value: 'stub', label: 'Test CRM' }]} />
          </div>
          <Button onClick={() => connect.mutate(null)} loading={connect.isPending}>Connect</Button>
        </div>
      )}
    </Panel>
  );
}

function Workflows() {
  const q = useQuery({ queryKey: ['workflows'], queryFn: () => get('/api/workflows') });
  const [f, setF] = useState({ name: '', trigger: 'booking.created', action: 'notify_owner_email', target: '' });
  const add = useSave(() => post('/api/workflows', { name: f.name, trigger: f.trigger, action: f.action, settings: f.action === 'webhook' ? { url: f.target } : f.action === 'notify_owner_sms' ? { phone: f.target } : {} }), 'Workflow added');
  const remove = useSave((id: string) => del(`/api/workflows/${id}`), 'Removed');
  return (
    <Panel title="Custom workflows" description="When something happens, notify someone or call your own system (signed webhook).">
      {q.data?.workflows.map((w: any) => (
        <div key={w.id} className="flex items-center gap-2 text-sm">
          <strong className="flex-1">{w.name}</strong>
          <Badge variant="outline">{w.trigger}</Badge>
          <Badge>{w.action.replace(/_/g, ' ')}</Badge>
          <Button variant="ghost" size="icon" aria-label="Delete workflow" onClick={() => remove.mutate(w.id)}><Trash2 /></Button>
        </div>
      ))}
      <form className="grid gap-2 sm:grid-cols-5" onSubmit={(e) => { e.preventDefault(); add.mutate(null); }}>
        <Input aria-label="Workflow name" placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
        <Select aria-label="When" value={f.trigger} onChange={(e) => setF({ ...f, trigger: e.target.value })} options={['booking.created', 'booking.cancelled', 'call.ended', 'message.taken', 'escalation.created'].map((t) => ({ value: t, label: t }))} />
        <Select aria-label="Then" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} options={[{ value: 'notify_owner_email', label: 'Email owners' }, { value: 'notify_owner_sms', label: 'Text a number' }, { value: 'webhook', label: 'Webhook' }]} />
        <Input aria-label="Target" placeholder={f.action === 'webhook' ? 'https://...' : f.action === 'notify_owner_sms' ? '+1...' : '(owners)'} disabled={f.action === 'notify_owner_email'} value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} />
        <Button type="submit" variant="outline" loading={add.isPending}><Plus /> Add</Button>
      </form>
    </Panel>
  );
}

function PrioritySupport() {
  const [f, setF] = useState({ subject: '', message: '' });
  const send = useSave(() => post('/api/support/priority', f), 'Sent. We reply within 4 business hours.');
  return (
    <Panel title="Priority support" description="Pro plans get a reply within 4 business hours.">
      <Input aria-label="Subject" placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
      <Textarea aria-label="Message" rows={4} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
      <Button onClick={() => send.mutate(null)} loading={send.isPending} disabled={!f.subject || !f.message}>Send</Button>
      {send.isSuccess ? <Alert variant="success" title="Message sent" /> : null}
    </Panel>
  );
}
